<template>
  <b-modal
    body-class="padding"
    button-size="sm"
    ok-title="Accept"
    size="lg"
    title="Upload Treatment Plan"
    title-tag="h5"
    cancel-title="Close"
    id="idTreatmentPlanUploadModal"
    cancel-variant="outline-primary"
    :ok-disabled="droppedFile === null && droppedDirectory.length === 0"
    @hidden="clearData"
    @ok="onOk"
  >
    <b-alert
      v-model="showAlert"
      variant="warning"
      style="font-size: 0.9em; padding-bottom: -0.7em"
      dismissible
    >
      Warning! Are you sure you want to upload the plan without providing all
      implant size information?
    </b-alert>
    <div class="padding">
      <b-row>
        <b-col>
          <b-form-file
            id="idFileDrop"
            v-model="droppedFile"
            browse-text="Browse for zip file"
            placeholder="Drag & Drop zip file here"
          ></b-form-file>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-file
            id="idDirectoryDrop"
            directory
            multiple
            v-model="droppedDirectory"
            browse-text="Select directory"
            placeholder="Drag & Drop a directory here"
            :file-name-formatter="formatNames"
          >
          </b-form-file>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <strong>Implant Size Information</strong>
        </b-col>
      </b-row>
      <b-row align-h="around">
        <b-col cols="3" v-if="this.implantData && this.implantData.length > 1">
          <label for="idSecondImplantParam">{{
            this.secondImplantParamLabel
          }}</label>
          <b-form-input
            size="sm"
            id="idSecondImplantParam"
            type="number"
            v-model="selectedImplantParam[1]"
          ></b-form-input>
        </b-col>
        <b-col cols="3" v-if="this.implantData && this.implantData.length > 0">
          <label for="idFirstImplantParam">{{
            this.firstImplantParamLabel
          }}</label>
          <b-form-input
            size="sm"
            id="idFirstImplantParam"
            type="number"
            v-model="selectedImplantParam[0]"
          ></b-form-input>
        </b-col>
        <b-col cols="3" v-if="this.implantData && this.implantData.length > 2">
          <label for="idThirdImplantParam">{{
            this.thirdImplantParamLabel
          }}</label>
          <b-form-input
            size="sm"
            id="idThirdImplantParam"
            type="number"
            v-model="selectedImplantParam[2]"
          ></b-form-input>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="1">
          <b-button
            id="idClearBtn"
            variant="primary"
            size="sm"
            @click="clearData"
            >Clear</b-button
          >
        </b-col>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
export default {
  data() {
    return {
      droppedFile: null,
      droppedDirectory: [],
      selectedImplantParam: [null, null, null],
      showAlert: false,
      secondTimeClickedOk: false,
    };
  },
  props: ["implantData", "patientDetailsFilled"],
  computed: {
    firstImplantParamLabel() {
      if (this.implantData && this.implantData.length > 0) {
        return this.implantData[0].text;
      } else {
        return null;
      }
    },
    firstImplantParamKey() {
      if (this.implantData && this.implantData.length > 0) {
        return this.implantData[0].value;
      } else {
        return null;
      }
    },
    secondImplantParamLabel() {
      if (this.implantData && this.implantData.length > 1) {
        return this.implantData[1].text;
      } else {
        return null;
      }
    },
    secondImplantParamKey() {
      if (this.implantData && this.implantData.length > 1) {
        return this.implantData[1].value;
      } else {
        return null;
      }
    },
    thirdImplantParamLabel() {
      if (this.implantData && this.implantData.length > 2) {
        return this.implantData[2].text;
      } else {
        return null;
      }
    },
    thirdImplantParamKey() {
      if (this.implantData && this.implantData.length > 2) {
        return this.implantData[2].value;
      } else {
        return null;
      }
    },
  },
  methods: {
    formatNames(files) {
      files = files.flat(10);
      if (files.length === 1) {
        return files[0].name;
      } else {
        return `${files.length} files selected`;
      }
    },
    onOk(bvEnt) {
      // All treatment and patient details need to be filled out before TP can be uploaded
      if (this.implantData && this.patientDetailsFilled) {
        // Checking if user filled out all the available implant fields
        let implantDataEmpty = false;
        this.selectedImplantParam.forEach((element, index) => {
          if (element === null && index < this.implantData.length) {
            implantDataEmpty = true;
          }
        });

        // Show a warning message if the user didn't fill them all out
        if (implantDataEmpty && !this.secondTimeClickedOk) {
          this.showAlert = true;
          this.secondTimeClickedOk = true;
          bvEnt.preventDefault();
          // Allow the user to still add a treatment plan without implant sizes on the second click
        } else {
          // Reset the flags
          this.secondTimeClickedOk = false;
          this.showAlert = false;

          // Put the implant info into a nice struct to pass back
          let implantInformationParameters = [];
          this.implantData.forEach((element, index) => {
            implantInformationParameters.push({
              key: element.value,
              value: this.selectedImplantParam[index],
            });
          });
          this.$emit("okClicked", {
            droppedFile: this.droppedFile,
            droppedDirectory: this.droppedDirectory.flat(10),
            implantInformationParameters: implantInformationParameters,
          });
        }
      } else {
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `All Treatment and Patient Details must be filled out before uploading a Treatment Plan.`,
        });
      }
    },
    clearData() {
      this.droppedFile = null;
      this.droppedDirectory = [];
      this.selectedImplantParam[0] = null;
      this.selectedImplantParam[1] = null;
      this.selectedImplantParam[2] = null;
      this.showAlert = false;
      this.secondTimeClickedOk = false;
    },
  },
};
</script>

<style scoped>
.padding {
  padding: 0.5rem 3rem 1rem 3rem;
}
.row {
  margin: 0.8rem 0;
}
</style>